import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContentHeaderComponent } from '@/shared/components/content-header/content-header.component';
import { PillTabsComponent } from '@/shared/ui-components/pill-tabs/pill-tabs.component';
import { Role } from '@/core/models/auth.interface';
import { AuthService } from '@/core/services/auth.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import { WmSummaryComponent } from './components/wm-summary/wm-summary.component';
import { WmRmClientsComponent } from './components/wm-rm-clients/wm-rm-clients.component';
import { WmDealsComponent } from './components/wm-deals/wm-deals.component';
import { WmPersonalizedDashboardComponent } from './components/wm-personalized-dashboard/wm-personalized-dashboard.component';

/* legacy code — chart/filter dashboard logic moved to components/wm-summary
import { effect } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { debounceTime, Subscription, finalize } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { ChartModule } from 'primeng/chart';
import { LayoutService } from '@/layout/service/layout.service';
import { WmStatsWidget } from './components/wm-statswidget/wm-statswidget';
import { filterPeriodValues } from '@/shared/data/generic';
import { Option } from '@/shared/enums/generic.enum';
import { LanguageService } from '@/shared/services/language.service';
import { FormsModule } from '@angular/forms';
import { NotificationsWidget } from '@/pages/dummy-templates/dashboard/components/notificationswidget/notificationswidget';
import { BestSellingWidget } from '@/pages/dummy-templates/dashboard/components/bestsellingwidget/bestsellingwidget';
import { RevenueStreamWidget } from '@/pages/dummy-templates/dashboard/components/revenuestreamwidget/revenuestreamwidget';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { SelectComponent } from '@/shared/ui-components/select/select.component';
import { LineChartComponent } from '@/shared/ui-components/charts/line-chart/line-chart.component';
import { DoughnutChartComponent } from '@/shared/ui-components/charts/doughnut-chart/doughnut-chart.component';
import { BarChartComponent } from '@/shared/ui-components/charts/bar-chart/bar-chart.component';
import { WealthManagerDashboardService } from './wealth-manager-dashboard.service';
import { AsyncState } from '@/shared/models/async-state.model';
import { WmTrendDto, WmVolumeByAssetClassDto } from './data-access/wealth-manager-dashboard.model';
*/

@Component({
    selector: 'app-wealth-manager-dashboard',
    standalone: true,
    imports: [
        TranslateModule,
        ContentHeaderComponent,
        PillTabsComponent,
        WmSummaryComponent,
        WmRmClientsComponent,
        WmDealsComponent,
        WmPersonalizedDashboardComponent,
    ],
    templateUrl: './wealth-manager-dashboard.component.html',
    styles: ``,
})
export class WmDashboardComponent implements OnInit {
    private readonly _translateService = inject(TranslateService);
    private readonly _authService = inject(AuthService);
    private readonly _userProfileService = inject(UserProfileService);

    /** Wealth manager sub-role flags used for dashboard layout. */
    public readonly isWmUsers = signal<boolean>(false);
    public readonly isWmDeals = signal<boolean>(false);
    public readonly isWmSuperUser = signal<boolean>(false);

    public readonly activeTabIndex = signal(0);

    public readonly dashboardTabs: string[] = [
        'WM.DASHBOARD.TABS.SUMMARY',
        'WM.DASHBOARD.TABS.RMS_CLIENTS',
        'WM.DASHBOARD.TABS.DEALS',
        // 'WM.DASHBOARD.TABS.PERSONALISED_DASHBOARD',
    ];

    constructor() {
        const role = this._authService.getAccessTokenRole();
        this.isWmUsers.set(role === Role.WEALTH_MANAGER_USERS);
        this.isWmDeals.set(role === Role.WEALTH_MANAGER_DEALS);
        this.isWmSuperUser.set(role === Role.WEALTH_MANAGER_SUPER_USER);
    }

    public ngOnInit() {
        this._userProfileService.getUserProfile();
    }

    public get dashboardHeaderTitle(): string {
        const profile = this._userProfileService.userProfileData();
        const baseTitle = this._translateService.instant('WM.DASHBOARD.TITLE') as string;
        const fullName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || profile?.name || '';
        const roleLabel = profile?.roleLabel ? (this._translateService.instant(profile.roleLabel) as string) : '';
        return [baseTitle, fullName, roleLabel].filter(Boolean).join(' - ');
    }

    public onTabChange(index: number): void {
        this.activeTabIndex.set(index);
    }

    /* legacy code — previous chart/filter members & methods now live in WmSummaryComponent
    lineData, barData, pieData, polarData, radarData, lineOptions, barOptions, pieOptions,
    polarOptions, radarOptions, doughnutChartData, filterPeriodValues, filterPeriodValue,
    filterPeriodValueReady, trendState, volumeByAssetClassState,
    onSelectFilterPeriodValue, applyDashboardPeriodFilter, volumeByAssetClassIconByKey,
    buildInvestmentsLineChart, initCharts, ngOnDestroy
    */
}
